import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { useResource } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";

interface Row {
    id: string;
    employee_id: string;
    employee_name: string;
    document_type_id: string | null;
    type_name: string | null;
    reference_number: string | null;
    expiry_date: string | null;
    verification_status: string;
    days_remaining: number | null;
    status: "none" | "current" | "expiring" | "expired";
}
interface ComplianceData {
    documents: Row[];
    counts: { expired: number; expiring: number; pendingVerification: number };
    window: number;
}
interface DocType { id: string; name: string }

const STATUS_CHIP: Record<Row["status"], string> = { current: "pass", expiring: "warn", expired: "block", none: "mute" };
const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("en-AU") : "—");

interface HidpaRow {
    id: string;
    employee_id: string;
    employee_name: string;
    service_name: string;
    assessor_name: string;
    assessor_credential: string | null;
    assessor_qualification: "qualified_health_practitioner" | "meets_descriptor_expectations";
    assessor_shortfall: boolean;
    last_delivered_at: string | null;
    absence_reassess_recommended: boolean;
    assessed_at: string;
    reassess_due: string;
    days_remaining: number;
    status: "current" | "due_soon" | "overdue";
}
interface HidpaService { id: string; name: string; is_high_intensity: boolean }
interface EmployeeOption { id: string; full_name: string }

const HIDPA_CHIP: Record<HidpaRow["status"], string> = { current: "pass", due_soon: "warn", overdue: "block" };

/**
 * The HIDPA competency register (migration 091): who is assessed for which
 * high-intensity support, by whom, and when the three-month reassessment
 * falls due. FLAG NEVER BLOCK - this table informs rostering decisions, it
 * never stops a shift.
 */
const HidpaCard = ({ mayManage }: { mayManage: boolean }) => {
    const register = useResource<HidpaRow[]>("/hidpa-competencies");
    const services = useResource<HidpaService[]>("/referral-services");
    const employees = useResource<EmployeeOption[]>(mayManage ? "/employees" : null);
    const [form, setForm] = useState({ employeeId: "", serviceId: "", assessorName: "", assessorCredential: "",
        assessorQualification: "qualified_health_practitioner", assessedAt: "" });
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const hidpaServices = (services.data ?? []).filter((s) => s.is_high_intensity);

    const record = async () => {
        if (!form.employeeId || !form.serviceId || !form.assessorName.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await api.post("/hidpa-competencies", {
                employeeId: form.employeeId,
                serviceId: form.serviceId,
                assessorName: form.assessorName.trim(),
                assessorQualification: form.assessorQualification,
                ...(form.assessorCredential.trim() ? { assessorCredential: form.assessorCredential.trim() } : {}),
                ...(form.assessedAt ? { assessedAt: form.assessedAt } : {}),
            });
            setForm({ employeeId: "", serviceId: "", assessorName: "", assessorCredential: "",
                assessorQualification: "qualified_health_practitioner", assessedAt: "" });
            register.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not record the assessment");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card" style={{ marginTop: "0.85rem" }}>
            <div className="card-head">
                <div>
                    <h2>High-intensity (HIDPA) competencies</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Who is assessed for which high-intensity support, by whom, and when the
                        three-month reassessment falls due. Informs rostering — never blocks a shift.
                    </p>
                </div>
            </div>
            <div className="card-body">
                {mayManage && (
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "end", marginBottom: "0.7rem" }}>
                        <label style={{ fontSize: "0.8rem" }}>Worker<br />
                            <select value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
                                <option value="">Choose…</option>
                                {(employees.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                            </select></label>
                        <label style={{ fontSize: "0.8rem" }}>High-intensity support<br />
                            <select value={form.serviceId} onChange={(e) => setForm({ ...form, serviceId: e.target.value })}>
                                <option value="">Choose…</option>
                                {hidpaServices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select></label>
                        <label style={{ fontSize: "0.8rem" }}>Assessor<br />
                            <input value={form.assessorName} placeholder="Name"
                                onChange={(e) => setForm({ ...form, assessorName: e.target.value })} /></label>
                        <label style={{ fontSize: "0.8rem" }}>Credential<br />
                            <input value={form.assessorCredential} placeholder="e.g. Registered Nurse"
                                onChange={(e) => setForm({ ...form, assessorCredential: e.target.value })} /></label>
                        <label style={{ fontSize: "0.8rem" }}>Assessor was<br />
                            <select value={form.assessorQualification}
                                onChange={(e) => setForm({ ...form, assessorQualification: e.target.value })}>
                                <option value="qualified_health_practitioner">Qualified health practitioner</option>
                                <option value="meets_descriptor_expectations">Meets Descriptor expectations</option>
                            </select></label>
                        <label style={{ fontSize: "0.8rem" }}>Assessed on<br />
                            <input type="date" value={form.assessedAt}
                                onChange={(e) => setForm({ ...form, assessedAt: e.target.value })} /></label>
                        <button className="btn small" disabled={busy || !form.employeeId || !form.serviceId || !form.assessorName.trim()}
                            onClick={() => void record()}>
                            {busy ? "Recording…" : "Record assessment"}
                        </button>
                    </div>
                )}
                {error && <div className="error-banner" style={{ marginBottom: "0.6rem" }}>{error}</div>}
                {(register.data ?? []).length === 0 ? (
                    <div className="empty">No assessments recorded yet.</div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%" }}>
                            <thead><tr>
                                <th style={{ textAlign: "left" }}>Worker</th>
                                <th style={{ textAlign: "left" }}>Support</th>
                                <th style={{ textAlign: "left" }}>Assessor</th>
                                <th>Assessed</th><th>Reassessment due</th><th>Status</th>
                            </tr></thead>
                            <tbody>
                                {(register.data ?? []).map((r) => (
                                    <tr key={r.id}>
                                        <td><Link to={`/employees?id=${r.employee_id}`}>{r.employee_name}</Link></td>
                                        <td>{r.service_name}</td>
                                        <td>
                                            {r.assessor_name}{r.assessor_credential
                                                ? <span className="muted"> · {r.assessor_credential}</span> : ""}
                                            {r.assessor_shortfall && (
                                                <span className="chip warn" style={{ marginLeft: 5 }}
                                                    title="This service expects a qualified health practitioner as assessor. Advisory only.">
                                                    assessor shortfall
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "center" }}>{fmt(r.assessed_at)}</td>
                                        <td style={{ textAlign: "center" }}>{fmt(r.reassess_due)}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <span className={`chip ${HIDPA_CHIP[r.status]}`}>
                                                {r.status === "due_soon" ? "due soon" : r.status}
                                            </span>
                                            {r.absence_reassess_recommended && (
                                                <span className="chip warn" style={{ marginLeft: 5 }}
                                                    title="Not delivered for longer than the service's absence threshold - the Descriptors recommend reassessment before supporting a client again.">
                                                    absence — reassess
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
const remainingLabel = (n: number | null) =>
    n === null ? "—" : n < 0 ? `${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} overdue` : `${n} day${n === 1 ? "" : "s"} left`;

export function Compliance() {
    const { mayQuality } = useAuth();
    const [type, setType] = useState("");
    const [status, setStatus] = useState("");
    const [sortDir, setSortDir] = useState<1 | -1>(1);
    const qs = new URLSearchParams();
    if (type) qs.set("type", type);
    if (status) qs.set("status", status);
    const data = useResource<ComplianceData>(`/compliance/documents${qs.toString() ? `?${qs}` : ""}`);
    const docTypes = useResource<DocType[]>("/document-types");

    const rows = useMemo(() => {
        const list = [...(data.data?.documents ?? [])];
        // Sort by soonest expiry (nulls last).
        list.sort((a, b) => {
            if (!a.expiry_date) return 1;
            if (!b.expiry_date) return -1;
            return (a.expiry_date < b.expiry_date ? -1 : a.expiry_date > b.expiry_date ? 1 : 0) * sortDir;
        });
        return list;
    }, [data.data, sortDir]);

    const counts = data.data?.counts;

    return (
        <div>
            <div className="stat-strip">
                <div className={`stat ${counts?.expired ? "bad" : "good"}`}>
                    <div className="num">{counts?.expired ?? 0}</div><div className="lbl">Expired</div>
                </div>
                <div className={`stat ${counts?.expiring ? "warn" : "good"}`}>
                    <div className="num">{counts?.expiring ?? 0}</div><div className="lbl">Expiring within {data.data?.window ?? 60} days</div>
                </div>
                <div className={`stat ${counts?.pendingVerification ? "warn" : "good"}`}>
                    <div className="num">{counts?.pendingVerification ?? 0}</div><div className="lbl">Pending verification</div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="toolbar" style={{ marginBottom: "0.6rem" }}>
                        <label>Type&nbsp;
                            <select value={type} onChange={(e) => setType(e.target.value)}>
                                <option value="">All types</option>
                                {(docTypes.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </label>
                        <label>&nbsp;&nbsp;Status&nbsp;
                            <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="">All</option>
                                <option value="expired">Expired</option>
                                <option value="expiring">Expiring</option>
                                <option value="current">Current</option>
                            </select>
                        </label>
                        <div className="spacer" />
                        <span className="muted">{rows.length} document{rows.length === 1 ? "" : "s"}</span>
                    </div>

                    {data.loading ? <div className="empty">Loading…</div>
                    : rows.length === 0 ? <div className="empty">No documents match.</div> : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%" }}>
                                <thead><tr>
                                    <th style={{ textAlign: "left" }}>Person</th>
                                    <th style={{ textAlign: "left" }}>Document</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => setSortDir((d) => (d === 1 ? -1 : 1))}>
                                        Expiry {sortDir === 1 ? "▲" : "▼"}
                                    </th>
                                    <th>Remaining</th><th>Verification</th><th>Status</th>
                                </tr></thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.id}>
                                            <td><Link to={`/employees?id=${r.employee_id}`}>{r.employee_name}</Link></td>
                                            <td>{r.type_name ?? "—"}{r.reference_number ? <span className="muted"> · {r.reference_number}</span> : ""}</td>
                                            <td style={{ textAlign: "center" }}>{fmt(r.expiry_date)}</td>
                                            <td style={{ textAlign: "center" }}>{remainingLabel(r.days_remaining)}</td>
                                            <td style={{ textAlign: "center" }}>
                                                <span className={`chip ${r.verification_status === "verified" ? "pass" : "warn"}`}>{r.verification_status}</span>
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span className={`chip ${STATUS_CHIP[r.status]}`}>{r.status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {(mayQuality("employees.hidpa.view") || mayQuality("employees.hidpa.manage")) && (
                <HidpaCard mayManage={mayQuality("employees.hidpa.manage")} />
            )}
        </div>
    );
}
